import { ReactNode, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { canDoAction } from '~/utils/helper/permission';
import { ISyncFailure, PAGE_SIZES, useDraftCollection } from '~/utils/hooks/useDraftCollection';
import { selectPermission, selectUser } from '~/thunks/auth/authSlice';
import { useAppSelector } from '~/utils/redux/hooks';
import { FormValues, ICollectionAdapter, IDraftRow, IFormField } from '~/utils/interface/setup';
import { getErrorMessage } from '~/utils/helper/error';
import ConfirmModal from './ConfirmModal';
import FormModal from './FormModal';

type Props<TRow> = {
  // i18n key prefix: <prefix>.title / .subtitle / .empty / .add / .edit
  i18nPrefix: string;
  // undefined: the app doesn't gate this screen by permission, so neither do we
  module?: string;
  // one action (e.g. manageAllergy) that allows add / edit / delete here, as the app does for Senior Medical
  permissionAction?: string;
  // when a module lacks an action the page uses (e.g. Photo has create + delete but no update), which one stands in
  actionMap?: Record<string, string>;
  adapter: ICollectionAdapter<TRow>;
  fields: IFormField[];
  defaultForm: FormValues;
  // names the row in the failed-sync list
  getTitle: (form: FormValues) => string;
  columns: ICollectionColumn[];
  rowActions?: ICollectionRowAction[];
  // sub-pages of a hub (e.g. Medical) get a link back to it
  backTo?: { path: string; labelKey: string };
};

// An immediate action on a saved row (not a draft change): asks to confirm, runs, then reports.
export interface ICollectionRowAction {
  key: string;
  labelKey: string;
  // i18n key of the confirmation text; {{name}} is the row's title
  confirmKey: string;
  successKey: string;
  run: (id: string) => Promise<unknown>;
}

export interface ICollectionColumn {
  key: string;
  labelKey: string;
  render: (form: FormValues) => ReactNode;
}

const STATUS_STYLE: Record<string, string> = {
  new: 'bg-accent text-accent-foreground',
  edited: 'bg-accent text-accent-foreground',
  deleted: 'bg-destructive/10 text-destructive',
};

const CollectionPage = <TRow,>({
  i18nPrefix,
  module,
  permissionAction,
  actionMap,
  adapter,
  fields,
  defaultForm,
  getTitle,
  columns,
  rowActions,
  backTo,
}: Props<TRow>) => {
  const { t } = useTranslation();
  const user = useAppSelector(selectUser);
  const permission = useAppSelector(selectPermission);
  const can = (action: string) => !module || canDoAction(user, permission, module, permissionAction ?? actionMap?.[action] ?? action);

  const collection = useDraftCollection(adapter);
  const [editing, setEditing] = useState<{ target?: IDraftRow<TRow> }>();
  const [syncing, setSyncing] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string }>();
  const [failures, setFailures] = useState<ISyncFailure[]>([]);
  const [confirming, setConfirming] = useState<{ action: ICollectionRowAction; item: IDraftRow<TRow> }>();
  const [acting, setActing] = useState(false);

  const tableColumns = useMemo<ColumnDef<IDraftRow<TRow>>[]>(
    () => [
      ...columns.map(
        (column, index): ColumnDef<IDraftRow<TRow>> => ({
          id: column.key,
          header: () => t(column.labelKey),
          cell: ({ row }) => (
            <>
              {column.render(row.original.form)}
              {index === 0 && row.original.status !== 'saved' && (
                <span className={`ml-2 rounded-full px-2 py-0.5 text-xs ${STATUS_STYLE[row.original.status]}`}>
                  {t(`pages.setup.status.${row.original.status}`)}
                </span>
              )}
            </>
          ),
        })
      ),
      {
        id: 'actions',
        header: () => <span className='sr-only'>{t('pages.setup.table.actions')}</span>,
        cell: ({ row }) => {
          const item = row.original;
          const isDeleted = item.status === 'deleted';
          return (
            <div className='whitespace-nowrap text-right text-xs'>
              {item.status === 'saved' &&
                item.id &&
                rowActions?.map((action) => (
                  <button
                    key={action.key}
                    type='button'
                    onClick={() => setConfirming({ action, item })}
                    className='rounded px-2 py-1 hover:bg-muted'
                  >
                    {t(action.labelKey)}
                  </button>
                ))}
              {(item.status === 'edited' || isDeleted) && (
                <button type='button' onClick={() => collection.undo(item)} className='rounded px-2 py-1 hover:bg-muted'>
                  {t('pages.setup.undo')}
                </button>
              )}
              {!isDeleted && (item.status === 'new' ? can('create') : can('update')) && (
                <button type='button' onClick={() => setEditing({ target: item })} className='rounded px-2 py-1 hover:bg-muted'>
                  {t('pages.setup.edit')}
                </button>
              )}
              {!isDeleted && (item.status === 'new' || can('delete')) && (
                <button
                  type='button'
                  onClick={() => collection.remove(item)}
                  className='rounded px-2 py-1 text-destructive hover:bg-destructive/10'
                >
                  {t('pages.setup.delete')}
                </button>
              )}
            </div>
          );
        },
      },
    ],
    // collection's handlers close over fresh state each render; the columns only need to rebuild with `rows`
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns, rowActions, t, collection.rows]
  );

  const table = useReactTable({
    data: collection.rows,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.key,
    manualPagination: true,
  });

  const pageFrom = collection.totalItems === 0 ? 0 : (collection.page - 1) * collection.pageSize + 1;
  const pageTo = Math.min(collection.page * collection.pageSize, collection.totalItems);

  const canSync = collection.pendingCount > 0 && !syncing;

  const handleSync = async () => {
    setSyncing(true);
    setFeedback(undefined);
    setFailures([]);
    try {
      const result = await collection.sync();
      setFailures(result.failures);
      setFeedback(
        result.failures.length === 0
          ? { type: 'success', message: t('pages.setup.syncSuccess') }
          : { type: 'error', message: t('pages.setup.syncPartial', { count: result.failures.length }) }
      );
    } finally {
      setSyncing(false);
    }
  };

  const handleRunAction = async () => {
    if (!confirming?.item.id) return;
    const { action, item } = confirming;
    setActing(true);
    setFeedback(undefined);
    try {
      await action.run(item.id as string);
      setFeedback({ type: 'success', message: t(action.successKey, { name: getTitle(item.form) }) });
    } catch (error) {
      const reason = getErrorMessage(error);
      setFeedback({ type: 'error', message: reason ?? t('pages.setup.unknownError') });
    } finally {
      setActing(false);
      setConfirming(undefined);
    }
  };

  const handleSubmit = (values: FormValues) => {
    if (editing?.target) collection.edit(editing.target, values);
    else collection.add(values);
    setEditing(undefined);
    setFeedback(undefined);
  };

  return (
    // fills the content area: the table takes only the height its rows need (scrolling inside when taller), pagination sits at the bottom
    <div className='flex h-full min-h-0 w-full flex-col gap-4'>
      {backTo && (
        <Link to={backTo.path} className='w-fit text-sm text-muted-foreground underline hover:text-foreground'>
          &larr; {t(backTo.labelKey)}
        </Link>
      )}
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div className='min-w-0 max-w-3xl'>
          <h1 className='text-xl font-semibold text-foreground'>{t(`${i18nPrefix}.title`)}</h1>
          <p className='text-sm text-muted-foreground'>{t(`${i18nPrefix}.subtitle`)}</p>
        </div>
        {/* ml-auto keeps the buttons on the right even when a long description pushes them to their own line */}
        <div className='ml-auto flex gap-2'>
          {can('create') && (
            <button
              type='button'
              onClick={() => setEditing({})}
              className='rounded-md border border-border px-4 py-2 text-sm font-semibold text-foreground hover:bg-muted'
            >
              {t(`${i18nPrefix}.add`)}
            </button>
          )}
          {(can('create') || can('update') || can('delete')) && (
            <button
              type='button'
              onClick={handleSync}
              disabled={!canSync}
              className='rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50'
            >
              {syncing ? t('pages.setup.syncing') : t('pages.setup.sync')}
            </button>
          )}
        </div>
      </div>

      {feedback && (
        <p className={`text-sm ${feedback.type === 'success' ? 'text-foreground' : 'text-destructive'}`} role='status'>
          {feedback.message}
        </p>
      )}
      {failures.length > 0 && (
        <ul className='flex list-disc flex-col gap-1 pl-5 text-sm text-destructive'>
          {failures.map((failure, index) => (
            <li key={index}>
              <span className='font-medium'>{getTitle(failure.form)}</span>: {failure.message ?? t('pages.setup.unknownError')}
            </li>
          ))}
        </ul>
      )}
      {collection.pendingCount > 0 && <p className='text-xs text-muted-foreground'>{t('pages.setup.unsynced')}</p>}

      {collection.loadFailed && <p className='text-sm text-destructive'>{t('pages.setup.loadFailed')}</p>}

      <input
        type='search'
        value={collection.searchInput}
        onChange={(e) => collection.setSearchInput(e.target.value)}
        placeholder={t('pages.setup.table.search')}
        aria-label={t('pages.setup.table.search')}
        className='w-full max-w-sm rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground'
      />

      <div
        className={`min-h-0 shrink overflow-auto rounded-lg border border-border bg-card transition-opacity ${
          collection.loading ? 'opacity-60' : ''
        }`}
        aria-busy={collection.loading}
      >
        <table className='w-full min-w-[40rem] border-separate border-spacing-0 text-left text-sm'>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    scope='col'
                    className='sticky top-0 z-10 border-b border-border bg-muted px-3 py-2 text-xs font-medium text-muted-foreground'
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 && (
              <tr>
                <td colSpan={tableColumns.length} className='px-3 py-6 text-center text-muted-foreground'>
                  {collection.loading
                    ? t('common.loading')
                    : t(collection.searchInput.trim() ? 'pages.setup.table.noResults' : `${i18nPrefix}.empty`)}
                </td>
              </tr>
            )}
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className={`hover:bg-muted/30 ${row.original.status === 'deleted' ? 'line-through opacity-60' : ''}`}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className='border-b border-border px-3 py-2 align-top'>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className='mt-auto flex shrink-0 flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground'>
        <span>{t('pages.setup.table.showing', { from: pageFrom, to: pageTo, total: collection.totalItems })}</span>
        <div className='flex items-center gap-2'>
          <label className='flex items-center gap-1'>
            {t('pages.setup.table.rowsPerPage')}
            <select
              value={collection.pageSize}
              onChange={(e) => collection.setPageSize(Number(e.target.value))}
              className='rounded-md border border-border bg-background px-1 py-1 text-foreground'
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
          <button
            type='button'
            onClick={() => collection.setPage(collection.page - 1)}
            disabled={collection.page <= 1 || collection.loading}
            className='rounded-md border border-border px-3 py-1 text-foreground hover:bg-muted disabled:opacity-50'
          >
            {t('pages.setup.table.previous')}
          </button>
          <span>
            {collection.page} / {collection.totalPages}
          </span>
          <button
            type='button'
            onClick={() => collection.setPage(collection.page + 1)}
            disabled={collection.page >= collection.totalPages || collection.loading}
            className='rounded-md border border-border px-3 py-1 text-foreground hover:bg-muted disabled:opacity-50'
          >
            {t('pages.setup.table.next')}
          </button>
        </div>
      </div>

      {confirming && (
        <ConfirmModal
          title={t(confirming.action.labelKey)}
          message={t(confirming.action.confirmKey, { name: getTitle(confirming.item.form) })}
          busy={acting}
          onConfirm={handleRunAction}
          onCancel={() => setConfirming(undefined)}
        />
      )}

      {editing && (
        <FormModal
          title={t(editing.target ? `${i18nPrefix}.edit` : `${i18nPrefix}.add`)}
          fields={fields}
          initial={editing.target ? editing.target.form : defaultForm}
          isExisting={!!editing.target && editing.target.status !== 'new'}
          onSubmit={handleSubmit}
          onCancel={() => setEditing(undefined)}
        />
      )}
    </div>
  );
};

export default CollectionPage;
